import React from 'react';
import { Home, Star, PlusCircle, LogOut, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { UserProfile } from '../types';

interface SidebarProps {
  onToggleFavorite: () => void;
  isFavorite: boolean;
  onNewProject: () => void;
  userProfile: UserProfile | null;
  onOpenProfile: () => void;
  onOpenSubscription?: () => void;
  onOpenPricing?: () => void;
  planName?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  onToggleFavorite,
  isFavorite,
  onNewProject,
  userProfile,
  onOpenProfile,
  onOpenSubscription,
  onOpenPricing,
  planName = 'Plano Free'
}) => {
  const { signOut } = useAuth();

  const getInitials = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name.charAt(0).toUpperCase();
    }
    return "TC";
  };

  return (
    <aside className="hidden lg:flex flex-col items-center py-8 w-20 bg-white shadow-lg shadow-slate-200/50 rounded-[32px] fixed left-4 top-4 bottom-4 z-50 border border-slate-100">
      <div className="mb-8 p-3 bg-slate-900 rounded-full text-white cursor-pointer hover:scale-110 transition-transform" onClick={onNewProject}>
        <Home size={20} />
      </div>

      <nav className="flex-1 flex flex-col gap-6 w-full items-center">
        <button
          className="p-3 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-full transition-all"
          title="Novo Projeto"
          onClick={onNewProject}
        >
          <PlusCircle size={24} />
        </button>
        <button
          onClick={onToggleFavorite}
          className={`p-3 rounded-full transition-all relative ${isFavorite ? 'text-yellow-400 hover:text-yellow-500' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-50'}`}
          title={isFavorite ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
        >
          <Star size={24} fill={isFavorite ? "currentColor" : "none"} />
          {isFavorite && <span className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full"></span>}
        </button>
      </nav>

      <div className="flex flex-col gap-6 mt-auto w-full items-center">
        {onOpenPricing && (
          <button
            onClick={onOpenPricing}
            className="p-3 text-blue-500 bg-blue-50 hover:text-white hover:bg-blue-600 rounded-full transition-all shadow-sm"
            title="Upgrade Pro"
          >
            <Zap size={24} />
          </button>
        )}

        <button
          onClick={() => signOut()}
          className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
          title="Sair"
        >
          <LogOut size={24} />
        </button>

        <div className="flex flex-col items-center gap-1.5 mt-2">
          <div
            className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider cursor-pointer hover:scale-105 transition-transform ${planName.toLowerCase().includes('pro')
              ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white shadow-sm'
              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
              }`}
            title="Gerenciar Assinatura"
            onClick={onOpenSubscription}
          >
            {planName.toLowerCase().includes('pro') ? 'PRO' : 'FREE'}
          </div>
          <button
            onClick={onOpenProfile}
            className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shadow-md hover:ring-2 hover:ring-blue-200 transition-all overflow-hidden"
            title="Editar Perfil"
          >
            {userProfile?.avatar_url ? (
              <img src={userProfile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              getInitials()
            )}
          </button>
        </div>
      </div>
    </aside>
  );
};